import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Eye, FileDown, ImageUp, Settings2, TriangleAlert } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Bytes } from '@/components/Bytes'

import { PendingButton } from '@/components/PendingButton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { getOrgBillingAdmin, listAllOrganizations } from '@/lib/admin/server'
import { formatMicroUsd, planPerGbMicroUsd } from '@/lib/billing/billing'
import {
  type AdminInvoiceRow,
  createInvoice,
  deleteIssuerProfile,
  getInvoiceLabelsSnapshot,
  type InvoiceStatus,
  type IssuerProfileRecord,
  listInvoices,
  listIssuerProfiles,
  listPrepaidTermHealth,
  markInvoicePaid,
  saveIssuerProfile,
  setInvoiceIssuer,
  voidInvoice,
} from '@/lib/billing/invoices'
import { cn } from '@/lib/utils'
import { AdminListCard } from './-components/AdminListCard'
import { buildInvoicePdf } from './-components/invoice-pdf'

export const Route = createFileRoute('/_authenticated/admin/invoices')({
  component: AdminInvoicesPage,
})

const STATUS_VARIANT: Record<
  InvoiceStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  open: 'secondary',
  overdue: 'destructive',
  paid: 'default',
  void: 'outline',
}

const STATUS_LABEL: Record<InvoiceStatus, string> = {
  open: 'Open',
  overdue: 'Overdue',
  paid: 'Paid',
  void: 'Void',
}

/** 20480 GB reads as "20 TB" on paperwork, smaller plans as plain GB. */
function formatDataAmount(includedGb: number): string {
  if (includedGb >= 1024 && includedGb % 1024 === 0) {
    return `${includedGb / 1024} TB`
  }
  return `${includedGb} GB`
}

function daysUntil(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000)
}

function AdminInvoicesPage() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    for (const key of ['admin-invoices', 'admin-term-health']) {
      void queryClient.invalidateQueries({ queryKey: [key] })
    }
  }
  const [creating, setCreating] = useState(false)
  const [labelsOpen, setLabelsOpen] = useState(false)
  const [paying, setPaying] = useState<AdminInvoiceRow | null>(null)
  const [voiding, setVoiding] = useState<AdminInvoiceRow | null>(null)
  const [viewing, setViewing] = useState<AdminInvoiceRow | null>(null)
  const [reissuing, setReissuing] = useState<AdminInvoiceRow | null>(null)

  const columns = useMemo<ColumnDef<AdminInvoiceRow>[]>(
    () => [
      {
        accessorKey: 'invoiceNumber',
        header: 'Number',
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {row.original.invoiceNumber}
          </span>
        ),
      },
      {
        accessorKey: 'organizationName',
        header: 'Organization',
        cell: ({ row }) => (
          <Link
            to="/admin/organizations/$orgId"
            params={{ orgId: row.original.organizationId }}
            className="hover:underline"
          >
            {row.original.organizationName}
          </Link>
        ),
      },
      {
        accessorKey: 'amountMicroUsd',
        header: 'Amount',
        cell: ({ row }) => (
          <span className="tabular-nums">
            {formatMicroUsd(row.original.amountMicroUsd)}
          </span>
        ),
      },
      {
        id: 'grants',
        header: 'Grants',
        cell: ({ row }) =>
          row.original.termMonths ? (
            <span className="text-muted-foreground text-xs">
              {row.original.planName ?? row.original.planSlug} ·{' '}
              {row.original.termMonths} mo
            </span>
          ) : row.original.dataGb ? (
            <span className="text-muted-foreground text-xs">
              {row.original.dataGb} GB this cycle
            </span>
          ) : (
            <span className="text-muted-foreground text-xs">No grant</span>
          ),
      },
      {
        accessorKey: 'dueAt',
        header: 'Due',
        cell: ({ row }) => new Date(row.original.dueAt).toLocaleDateString(),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={STATUS_VARIANT[row.original.status]}>
            {STATUS_LABEL[row.original.status]}
          </Badge>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const inv = row.original
          return (
            <div className="flex items-center justify-end gap-1">
              <Button variant="ghost" size="sm" onClick={() => setViewing(inv)}>
                <Eye data-icon="inline-start" />
                View
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    More
                  </Button>
                </DropdownMenuTrigger>
                {/* Opens leftward so the last rows of the table never push
                    the menu below the viewport. */}
                <DropdownMenuContent side="left" align="start">
                  <DropdownMenuGroup>
                    {inv.status === 'open' || inv.status === 'overdue' ? (
                      <DropdownMenuItem onClick={() => setPaying(inv)}>
                        Mark paid
                      </DropdownMenuItem>
                    ) : null}
                    {inv.status !== 'void' ? (
                      <DropdownMenuItem onClick={() => setReissuing(inv)}>
                        Change issuer
                      </DropdownMenuItem>
                    ) : null}
                    {inv.status !== 'void' ? (
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() => setVoiding(inv)}
                      >
                        Void
                      </DropdownMenuItem>
                    ) : null}
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        },
      },
    ],
    [],
  )

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="font-semibold text-2xl">Invoices</h1>
          <p className="text-muted-foreground text-sm">
            Invoices for direct-payment customers. Marking an invoice paid
            records the payment and applies what the invoice grants.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setLabelsOpen(true)}>
            <Settings2 data-icon="inline-start" />
            Issuer details
          </Button>
          <Button onClick={() => setCreating(true)}>New invoice</Button>
        </div>
      </div>

      <TermHealth />

      <AdminListCard<AdminInvoiceRow>
        title="All invoices"
        noun="invoice"
        queryKey={['admin-invoices']}
        queryFn={() => listInvoices()}
        columns={columns}
        emptyMessage="No invoices yet. Issue the first one with New invoice."
        searchAccessor={(r) =>
          `${r.invoiceNumber} ${r.organizationName} ${r.status}`
        }
        searchPlaceholder="Search by number, org, or status"
      />

      <CreateInvoiceDialog
        open={creating}
        onOpenChange={setCreating}
        onDone={invalidate}
      />
      <MarkPaidDialog
        // Keyed by target: form state must reset per invoice.
        key={paying?.id ?? 'none'}
        invoice={paying}
        onOpenChange={(o) => !o && setPaying(null)}
        onDone={invalidate}
      />
      <VoidDialog
        invoice={voiding}
        onOpenChange={(o) => !o && setVoiding(null)}
        onDone={invalidate}
      />
      <PdfPreviewDialog
        invoice={viewing}
        onOpenChange={(o) => !o && setViewing(null)}
      />
      <ChangeIssuerDialog
        invoice={reissuing}
        onOpenChange={(o) => !o && setReissuing(null)}
      />
      <IssuerProfilesDialog open={labelsOpen} onOpenChange={setLabelsOpen} />
    </div>
  )
}

/** Renewals needing attention, as one compact scrollable card: prepaid orgs
 *  with no renewal set up, then active terms by how soon they end. */
function TermHealth() {
  const { data } = useQuery({
    queryKey: ['admin-term-health'],
    queryFn: () => listPrepaidTermHealth(),
  })
  if (!data) return null
  const soon = data.terms.filter((t) => daysUntil(t.termEndAt) <= 30)
  if (soon.length === 0 && data.orphans.length === 0) return null
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <TriangleAlert className="size-4 text-destructive" />
          Renewals needing attention
        </CardTitle>
        <CardDescription>
          Direct-payment terms ending within 30 days, and direct-payment
          organizations with no renewal scheduled.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex max-h-56 flex-col gap-1 overflow-y-auto pr-1">
          {data.orphans.map((o) => (
            <div
              key={o.organizationId}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2"
            >
              <div className="min-w-0">
                <Link
                  to="/admin/organizations/$orgId"
                  params={{ orgId: o.organizationId }}
                  className="font-medium text-sm hover:underline"
                >
                  {o.organizationName}
                </Link>
                <p className="text-muted-foreground text-xs">
                  No renewal scheduled. Access{' '}
                  {o.planPeriodEnd
                    ? `ends ${new Date(o.planPeriodEnd).toLocaleDateString()}`
                    : 'has no end date recorded'}
                  . Issue a plan term invoice, or gift months from the
                  organization page.
                </p>
              </div>
              <Badge variant="destructive">no renewal</Badge>
            </div>
          ))}
          {soon.map((t) => {
            const days = daysUntil(t.termEndAt)
            return (
              <div
                key={t.organizationId}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2"
              >
                <div className="min-w-0">
                  <Link
                    to="/admin/organizations/$orgId"
                    params={{ orgId: t.organizationId }}
                    className="font-medium text-sm hover:underline"
                  >
                    {t.organizationName}
                  </Link>
                  <p className="text-muted-foreground text-xs">
                    {t.planSlug} ·{' '}
                    <Bytes value={t.grantBytesPerCycle} unit="gb" /> per cycle ·
                    term ends {new Date(t.termEndAt).toLocaleDateString()}
                  </p>
                </div>
                <Badge variant={days <= 7 ? 'destructive' : 'secondary'}>
                  {days}d left
                </Badge>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

function CreateInvoiceDialog({
  open,
  onOpenChange,
  onDone,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onDone: () => void
}) {
  const [orgId, setOrgId] = useState('')
  const [planSlug, setPlanSlug] = useState('')
  const [months, setMonths] = useState('1')
  const [amountUsd, setAmountUsd] = useState('')
  const [dueAt, setDueAt] = useState(() =>
    new Date(Date.now() + 14 * 86_400_000).toISOString().slice(0, 10),
  )
  const [description, setDescription] = useState('')
  const [descriptionTouched, setDescriptionTouched] = useState(false)
  const [billTo, setBillTo] = useState('')
  const [numberOverride, setNumberOverride] = useState('')
  const [kind, setKind] = useState<'term' | 'data' | 'money'>('term')
  const [dataGb, setDataGb] = useState('')
  const [itemize, setItemize] = useState(true)
  const [issuerId, setIssuerId] = useState('')
  // A code from /admin/coupons. Validated server-side against the coupons
  // table — this field cannot invent a discount, only name one that exists.
  const [couponCode, setCouponCode] = useState('')
  const [requestKey, setRequestKey] = useState(() => crypto.randomUUID())

  const orgs = useQuery({
    queryKey: ['admin-orgs-for-invoice'],
    queryFn: () => listAllOrganizations(),
    enabled: open,
    // Orgs are created through better-auth, outside this query cache, so no
    // invalidation ever reaches this key. The global 30s staleTime then
    // serves a list missing the org that was just created — refetch on every
    // dialog open instead; the list is small.
    staleTime: 0,
    refetchOnMount: 'always',
  })
  const issuers = useQuery({
    queryKey: ['admin-issuer-profiles'],
    queryFn: () => listIssuerProfiles(),
    enabled: open,
  })
  const issuerProfiles = issuers.data ?? []
  const effectiveIssuerId =
    issuerId ||
    (issuerProfiles.find((p) => p.isDefault) ?? issuerProfiles[0])?.id ||
    ''
  const billing = useQuery({
    queryKey: ['admin-org-billing', orgId],
    queryFn: () => getOrgBillingAdmin({ data: { organizationId: orgId } }),
    enabled: open && !!orgId,
  })
  const plans = billing.data?.plans ?? []
  const plan = plans.find((p) => p.slug === planSlug)
  const monthsN = Math.floor(Number(months))

  // The line item, prefilled in the documented format and freely editable.
  // It tracks org/plan changes until the operator types, then their text wins.
  const orgName = (orgs.data ?? []).find((o) => o.id === orgId)?.name
  const dataGbN = Math.floor(Number(dataGb))
  const defaultDescription =
    kind === 'data' && orgName && dataGbN >= 1
      ? `Masklabs - ${orgName} - Additional data (${formatDataAmount(dataGbN)})`
      : kind === 'term' && plan && orgName
        ? `Masklabs - ${orgName} - ${plan.name} (${formatDataAmount(plan.includedGb)}) @ ${formatMicroUsd(planPerGbMicroUsd(plan))} per GB`
        : ''
  const effectiveDescription = descriptionTouched
    ? description
    : defaultDescription

  // Prefill the amount: term invoices use monthly price x months; data
  // invoices use GB x the selected plan's per-GB rate. Always editable.
  const suggested =
    kind === 'term' && plan && monthsN >= 1
      ? ((plan.priceMonthlyMicroUsd * monthsN) / 1e6).toFixed(2)
      : kind === 'data' && plan && dataGbN >= 1
        ? ((planPerGbMicroUsd(plan) * dataGbN) / 1e6).toFixed(2)
        : ''
  const effectiveAmount = amountUsd !== '' ? amountUsd : suggested

  function reset() {
    setOrgId('')
    setPlanSlug('')
    setMonths('1')
    setAmountUsd('')
    setDescription('')
    setDescriptionTouched(false)
    setBillTo('')
    setNumberOverride('')
    setKind('term')
    setDataGb('')
    setItemize(true)
    setIssuerId('')
    setCouponCode('')
    setRequestKey(crypto.randomUUID())
  }

  const mutation = useMutation({
    mutationFn: () =>
      createInvoice({
        data: {
          organizationId: orgId,
          amountUsd: Number(effectiveAmount),
          dueAt: new Date(dueAt).toISOString(),
          description: effectiveDescription.trim() || undefined,
          billTo: billTo.trim() || undefined,
          planSlug: kind === 'term' ? planSlug || undefined : undefined,
          termMonths: kind === 'term' ? monthsN : undefined,
          dataGb: kind === 'data' ? dataGbN : undefined,
          itemize: kind !== 'money' && itemize,
          numberOverride: numberOverride.trim() || undefined,
          issuerProfileId: effectiveIssuerId || undefined,
          couponCode: couponCode.trim() || undefined,
          requestKey,
        },
      }),
    onSuccess: (r) => {
      onDone()
      onOpenChange(false)
      reset()
      toast.success(`Issued ${r.invoiceNumber}.`)
    },
    onError: (e) =>
      toast.error('Could not issue the invoice', {
        description: (e as Error).message,
      }),
  })

  const valid =
    !!orgId &&
    Number(effectiveAmount) >= 0 &&
    effectiveAmount !== '' &&
    !!dueAt &&
    (kind === 'money' ||
      (kind === 'term' && !!planSlug && monthsN >= 1 && monthsN <= 12) ||
      (kind === 'data' && dataGbN >= 1 && dataGbN <= 100_000))

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (!o) reset()
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New invoice</DialogTitle>
          <DialogDescription>
            The price and what it grants are stated separately, since the amount
            is whatever was negotiated. Paying it in full applies the term.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (valid) mutation.mutate()
          }}
          className="flex flex-col gap-4"
        >
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="inv-org">Organization</FieldLabel>
              <Select
                value={orgId}
                onValueChange={(v) => {
                  setOrgId(v)
                  // Plans are per-org; a stale slug would keep pricing the
                  // previous org's plan behind the placeholder.
                  setPlanSlug('')
                  setAmountUsd('')
                }}
              >
                <SelectTrigger id="inv-org">
                  <SelectValue placeholder="Choose an organization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {(orgs.data ?? []).map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>

            <Field>
              <FieldLabel htmlFor="inv-billto">Invoiced to</FieldLabel>
              <Textarea
                id="inv-billto"
                value={billTo}
                rows={3}
                placeholder={
                  (orgs.data ?? []).find((o) => o.id === orgId)?.name ??
                  'Customer legal name and address'
                }
                onChange={(e) => setBillTo(e.target.value)}
              />
              <FieldDescription>
                Rendered as the INVOICED TO block on the PDF. Blank uses the
                organization name.
              </FieldDescription>
            </Field>

            {issuerProfiles.length > 1 ? (
              <Field>
                <FieldLabel htmlFor="inv-issuer">Issued by</FieldLabel>
                <Select value={effectiveIssuerId} onValueChange={setIssuerId}>
                  <SelectTrigger id="inv-issuer">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {issuerProfiles.map((prof) => (
                        <SelectItem key={prof.id} value={prof.id}>
                          {prof.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FieldDescription>
                  The issuer profile whose logo, address, and bank details
                  appear on the PDF.
                </FieldDescription>
              </Field>
            ) : null}

            <Field>
              <FieldLabel>Invoice type</FieldLabel>
              <Select
                value={kind}
                onValueChange={(v) => setKind(v as 'term' | 'data' | 'money')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="term">Plan term</SelectItem>
                    <SelectItem value="data">Additional data</SelectItem>
                    <SelectItem value="money">No grant</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <FieldDescription>
                {kind === 'term'
                  ? 'When paid: extends the plan term by the selected months.'
                  : kind === 'data'
                    ? 'When paid: adds data to the current billing cycle. Unused data expires at cycle end.'
                    : 'Records revenue only. Grants nothing.'}
              </FieldDescription>
            </Field>

            {kind === 'data' ? (
              <Field>
                <FieldLabel htmlFor="inv-datagb">Data (GB)</FieldLabel>
                <Input
                  id="inv-datagb"
                  type="number"
                  min={1}
                  max={100000}
                  value={dataGb}
                  onChange={(e) => setDataGb(e.target.value)}
                />
              </Field>
            ) : null}

            {kind === 'term' ? (
              <>
                <Field>
                  <FieldLabel htmlFor="inv-plan">Plan</FieldLabel>
                  <Select
                    value={planSlug}
                    onValueChange={setPlanSlug}
                    disabled={!orgId}
                  >
                    <SelectTrigger id="inv-plan">
                      <SelectValue
                        placeholder={
                          orgId ? 'Choose a plan' : 'Pick an organization first'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {plans.map((p) => (
                          <SelectItem key={p.slug} value={p.slug}>
                            {p.name} ({p.includedGb} GB/mo,{' '}
                            {formatMicroUsd(p.priceMonthlyMicroUsd)}/mo)
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <FieldDescription>
                    Custom per-org plans are created from the organization page.
                  </FieldDescription>
                </Field>
                <Field>
                  <FieldLabel htmlFor="inv-months">Term (months)</FieldLabel>
                  <Input
                    id="inv-months"
                    type="number"
                    min={1}
                    max={12}
                    value={months}
                    onChange={(e) => setMonths(e.target.value)}
                  />
                </Field>
              </>
            ) : null}

            <Field>
              <FieldLabel htmlFor="inv-amount">Amount (USD)</FieldLabel>
              <Input
                id="inv-amount"
                type="number"
                step="0.01"
                value={effectiveAmount}
                onChange={(e) => setAmountUsd(e.target.value)}
              />
              <FieldDescription>
                Prefilled from the plan price. Change it to whatever was agreed.
              </FieldDescription>
            </Field>
            {kind !== 'money' ? (
              <Field orientation="horizontal">
                <Switch
                  id="inv-itemize"
                  checked={itemize}
                  onCheckedChange={(v: boolean) => setItemize(v)}
                />
                <FieldLabel htmlFor="inv-itemize" className="font-normal">
                  Per-GB breakdown on the PDF
                </FieldLabel>
              </Field>
            ) : null}
            {kind !== 'money' && !itemize ? (
              <p className="text-muted-foreground text-xs">
                The PDF shows a single line with the amount. The grant is
                unaffected.
              </p>
            ) : null}

            <Field>
              <FieldLabel htmlFor="inv-due">Due date</FieldLabel>
              <Input
                id="inv-due"
                type="date"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                required
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="inv-desc">Line item</FieldLabel>
              <Input
                id="inv-desc"
                value={effectiveDescription}
                placeholder="The line item on the PDF"
                onChange={(e) => {
                  setDescription(e.target.value)
                  setDescriptionTouched(e.target.value !== '')
                }}
              />
              <FieldDescription>
                The single line item on the PDF. Prefilled from the organization
                and plan; editable. Clear the field to restore the generated
                text.
              </FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="inv-coupon">
                Discount code (optional)
              </FieldLabel>
              <Input
                id="inv-coupon"
                className="font-mono"
                value={couponCode}
                placeholder="Leave blank for no discount"
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              />
              <FieldDescription>
                Must be a live code from Coupons. The amount above is the list
                price; the discount comes off it and appears as its own line on
                the PDF. Issuing does not redeem the code — marking the invoice
                paid does.
              </FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="inv-number">
                Invoice number override
              </FieldLabel>
              <Input
                id="inv-number"
                className="font-mono"
                value={numberOverride}
                placeholder="Leave blank to auto-number"
                onChange={(e) => setNumberOverride(e.target.value)}
              />
              <FieldDescription>
                Blank assigns the next number in the series. Enter a number only
                to import an externally issued invoice.
              </FieldDescription>
            </Field>
          </FieldGroup>
          <DialogFooter>
            <PendingButton
              type="submit"
              pending={mutation.isPending}
              disabled={!valid}
            >
              Issue invoice
            </PendingButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function MarkPaidDialog({
  invoice,
  onOpenChange,
  onDone,
}: {
  invoice: AdminInvoiceRow | null
  onOpenChange: (open: boolean) => void
  onDone: () => void
}) {
  const [method, setMethod] = useState('wire')
  const [receivedAt, setReceivedAt] = useState(() => {
    // Local calendar date; toISOString() would shift across midnight UTC.
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })
  const [note, setNote] = useState('')
  const [requestKey, setRequestKey] = useState(() => crypto.randomUUID())

  const mutation = useMutation({
    mutationFn: () =>
      markInvoicePaid({
        data: {
          invoiceId: invoice?.id ?? '',
          method,
          receivedAt: new Date(receivedAt).toISOString(),
          note: note.trim() || undefined,
          requestKey,
        },
      }),
    onSuccess: (r) => {
      onDone()
      onOpenChange(false)
      setRequestKey(crypto.randomUUID())
      setNote('')
      if (!r.paid) {
        toast.info(`${r.invoiceNumber} was already paid. Nothing changed.`)
        return
      }
      toast.success(
        r.termEndAt
          ? `${r.invoiceNumber} paid. Term runs through ${new Date(r.termEndAt).toLocaleDateString()}.`
          : `${r.invoiceNumber} paid.`,
      )
    },
    onError: (e) =>
      toast.error('Could not mark the invoice paid', {
        description: (e as Error).message,
      }),
  })

  return (
    <Dialog open={!!invoice} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark {invoice?.invoiceNumber} paid</DialogTitle>
          <DialogDescription>
            Records {invoice ? formatMicroUsd(invoice.amountMicroUsd) : ''}{' '}
            received in full from {invoice?.organizationName}
            {invoice?.termMonths
              ? `, and extends the plan term by ${invoice.termMonths} month${invoice.termMonths === 1 ? '' : 's'}`
              : invoice?.dataGb
                ? `, and adds ${invoice.dataGb} GB to the current billing cycle`
                : ''}
            . Partial payments are not supported.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            mutation.mutate()
          }}
          className="flex flex-col gap-4"
        >
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="pay-method">Method</FieldLabel>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger id="pay-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="wire">Bank wire</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="pay-received">Date received</FieldLabel>
              <Input
                id="pay-received"
                type="date"
                value={receivedAt}
                onChange={(e) => setReceivedAt(e.target.value)}
                required
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="pay-note">Internal note</FieldLabel>
              <Input
                id="pay-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </Field>
          </FieldGroup>
          <DialogFooter>
            <PendingButton type="submit" pending={mutation.isPending}>
              Mark paid
            </PendingButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function VoidDialog({
  invoice,
  onOpenChange,
  onDone,
}: {
  invoice: AdminInvoiceRow | null
  onOpenChange: (open: boolean) => void
  onDone: () => void
}) {
  const [reason, setReason] = useState('')
  const mutation = useMutation({
    mutationFn: () =>
      voidInvoice({ data: { invoiceId: invoice?.id ?? '', reason } }),
    onSuccess: () => {
      onDone()
      onOpenChange(false)
      setReason('')
      toast.success(`${invoice?.invoiceNumber} voided.`)
    },
    onError: (e) =>
      toast.error('Could not void the invoice', {
        description: (e as Error).message,
      }),
  })
  return (
    <Dialog open={!!invoice} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Void {invoice?.invoiceNumber}</DialogTitle>
          <DialogDescription>
            The invoice keeps its number and remains listed as void.
            {invoice?.paidAt
              ? ' The payment is reversed in the revenue records. Any term or data it granted is not clawed back automatically.'
              : ''}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (reason.trim().length >= 3) mutation.mutate()
          }}
          className="flex flex-col gap-4"
        >
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="void-reason">Reason</FieldLabel>
              <Input
                id="void-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                autoFocus
              />
            </Field>
          </FieldGroup>
          <DialogFooter>
            <PendingButton
              type="submit"
              variant="destructive"
              pending={mutation.isPending}
              disabled={reason.trim().length < 3}
            >
              Void invoice
            </PendingButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** In-app PDF viewer: builds the document from the invoice plus its frozen
 *  label snapshot, shows it inline, and offers the download from the same
 *  built bytes so what you saw is what you saved. */
function PdfPreviewDialog({
  invoice,
  onOpenChange,
}: {
  invoice: AdminInvoiceRow | null
  onOpenChange: (open: boolean) => void
}) {
  const [pdf, setPdf] = useState<{
    forId: string
    blobUrl: string
    download: () => void
  } | null>(null)

  const target = invoice?.id ?? null
  if (target && pdf?.forId !== target && invoice) {
    setPdf({ forId: target, blobUrl: '', download: () => {} })
    void (async () => {
      try {
        const labels = await getInvoiceLabelsSnapshot({
          data: { invoiceId: invoice.id },
        })
        const built = await buildInvoicePdf(invoice, labels)
        setPdf({ forId: invoice.id, ...built })
      } catch (e) {
        onOpenChange(false)
        toast.error('Could not build the PDF', {
          description: (e as Error).message,
        })
      }
    })()
  }

  function close(open: boolean) {
    if (!open && pdf?.blobUrl) URL.revokeObjectURL(pdf.blobUrl)
    if (!open) setPdf(null)
    onOpenChange(open)
  }

  return (
    <Dialog open={!!invoice} onOpenChange={close}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{invoice?.invoiceNumber}</DialogTitle>
          <DialogDescription>
            {invoice?.organizationName} ·{' '}
            {invoice ? formatMicroUsd(invoice.amountMicroUsd) : ''}
          </DialogDescription>
        </DialogHeader>
        {pdf?.blobUrl ? (
          <iframe
            src={pdf.blobUrl}
            title={`Invoice ${invoice?.invoiceNumber ?? ''}`}
            className="h-[70vh] w-full rounded-md border bg-white"
          />
        ) : (
          <div className="flex h-[70vh] items-center justify-center text-muted-foreground text-sm">
            Building the PDF…
          </div>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => pdf?.download()}
            disabled={!pdf?.blobUrl}
          >
            <FileDown data-icon="inline-start" />
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Re-freeze which issuer profile an existing invoice carries. */
function ChangeIssuerDialog({
  invoice,
  onOpenChange,
}: {
  invoice: AdminInvoiceRow | null
  onOpenChange: (open: boolean) => void
}) {
  const [profileId, setProfileId] = useState('')
  const { data: profiles } = useQuery({
    queryKey: ['admin-issuer-profiles'],
    queryFn: () => listIssuerProfiles(),
    enabled: !!invoice,
  })
  const effective =
    profileId ||
    ((profiles ?? []).find((p) => p.isDefault) ?? (profiles ?? [])[0])?.id ||
    ''
  const mutation = useMutation({
    mutationFn: () =>
      setInvoiceIssuer({
        data: { invoiceId: invoice?.id ?? '', issuerProfileId: effective },
      }),
    onSuccess: (r) => {
      onOpenChange(false)
      setProfileId('')
      toast.success(`${r.invoiceNumber} now carries the selected issuer.`)
    },
    onError: (e) =>
      toast.error('Could not change the issuer', {
        description: (e as Error).message,
      }),
  })
  return (
    <Dialog open={!!invoice} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change issuer for {invoice?.invoiceNumber}</DialogTitle>
          <DialogDescription>
            Replaces the logo, address, and bank details frozen on this invoice.
            The next PDF download reflects it.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (effective) mutation.mutate()
          }}
          className="flex flex-col gap-4"
        >
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="reissue-profile">Issuer profile</FieldLabel>
              <Select value={effective} onValueChange={setProfileId}>
                <SelectTrigger id="reissue-profile">
                  <SelectValue placeholder="Choose a profile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {(profiles ?? []).map((prof) => (
                      <SelectItem key={prof.id} value={prof.id}>
                        {prof.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>
          </FieldGroup>
          <DialogFooter>
            <PendingButton
              type="submit"
              pending={mutation.isPending}
              disabled={!effective}
            >
              Change issuer
            </PendingButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Issuer profiles: several billing identities (entity name, logo, letterhead
 * address, bank details), one default. The invoice create dialog picks one
 * and freezes its fields onto the invoice, so edits here never touch
 * already-issued paperwork.
 */
function IssuerProfilesDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const { data: profiles } = useQuery({
    queryKey: ['admin-issuer-profiles'],
    queryFn: () => listIssuerProfiles(),
    enabled: open,
  })
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['admin-issuer-profiles'] })

  // null = list view; 'new' = creating; otherwise the profile id being edited.
  const [editing, setEditing] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [logo, setLogo] = useState<string | null>(null)
  const [fromAddress, setFromAddress] = useState('')
  const [bankDetails, setBankDetails] = useState('')
  const [isDefault, setIsDefault] = useState(false)

  function startNew() {
    setEditing('new')
    setName('')
    setLogo(null)
    setFromAddress('')
    setBankDetails('')
    setIsDefault((profiles?.length ?? 0) === 0)
  }
  function startEdit(prof: IssuerProfileRecord) {
    setEditing(prof.id)
    setName(prof.name)
    setLogo(prof.logo)
    setFromAddress(prof.fromAddress ?? '')
    setBankDetails(prof.bankDetails ?? '')
    setIsDefault(prof.isDefault)
  }

  const save = useMutation({
    mutationFn: () =>
      saveIssuerProfile({
        data: {
          id: editing === 'new' ? undefined : (editing ?? undefined),
          name,
          logo,
          fromAddress,
          bankDetails,
          isDefault,
        },
      }),
    onSuccess: () => {
      void refresh()
      setEditing(null)
      toast.success('Profile saved.')
    },
    onError: (e) =>
      toast.error('Could not save the profile', {
        description: (e as Error).message,
      }),
  })
  const remove = useMutation({
    mutationFn: (id: string) => deleteIssuerProfile({ data: { id } }),
    onSuccess: () => void refresh(),
    onError: (e) =>
      toast.error('Could not delete the profile', {
        description: (e as Error).message,
      }),
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (!o) setEditing(null)
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Issuer profiles</DialogTitle>
          <DialogDescription>
            The identities invoices are issued under: logo, address, and bank
            details. Each invoice freezes its chosen profile at issue time, so
            edits here never change existing invoices.
          </DialogDescription>
        </DialogHeader>

        {editing === null ? (
          <div className="flex flex-col gap-3">
            {(profiles ?? []).map((prof) => (
              <div
                key={prof.id}
                className="flex items-center justify-between gap-3 rounded-md border p-3"
              >
                <div className="flex min-w-0 items-center gap-3">
                  {prof.logo ? (
                    <img
                      src={prof.logo}
                      alt=""
                      className="h-9 w-16 shrink-0 rounded border bg-white object-contain p-0.5"
                    />
                  ) : (
                    <div className="flex h-9 w-16 shrink-0 items-center justify-center rounded border border-dashed text-[10px] text-muted-foreground">
                      no logo
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 font-medium text-sm">
                      <span className="truncate">{prof.name}</span>
                      {prof.isDefault ? (
                        <Badge variant="secondary">Default</Badge>
                      ) : null}
                    </p>
                    <p className="truncate text-muted-foreground text-xs">
                      {prof.fromAddress?.split('\n')[0] ?? 'No address'}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => startEdit(prof)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove.mutate(prof.id)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
            {(profiles ?? []).length === 0 ? (
              <p className="rounded-md border border-dashed p-6 text-center text-muted-foreground text-sm">
                No issuer profiles yet. Add one before issuing invoices.
              </p>
            ) : null}
            <DialogFooter>
              <Button onClick={startNew}>Add profile</Button>
            </DialogFooter>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (name.trim()) save.mutate()
            }}
            className="flex flex-col gap-4"
          >
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="prof-name">Name</FieldLabel>
                <Input
                  id="prof-name"
                  value={name}
                  placeholder="Masklabs LLC"
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoFocus
                />
                <FieldDescription>
                  Shown in the issuer picker. Not rendered on the PDF.
                </FieldDescription>
              </Field>
              <Field>
                <FieldLabel>Logo</FieldLabel>
                <LogoDropzone logo={logo} onChange={setLogo} />
              </Field>
              <Field>
                <FieldLabel htmlFor="prof-from">Address</FieldLabel>
                <Textarea
                  id="prof-from"
                  value={fromAddress}
                  rows={3}
                  placeholder={'Masklabs\n123 Example St\nCity, ST 00000'}
                  onChange={(e) => setFromAddress(e.target.value)}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="prof-bank">Bank details</FieldLabel>
                <Textarea
                  id="prof-bank"
                  value={bankDetails}
                  rows={3}
                  placeholder={
                    'Bank: Example Bank\nRouting: 000000000\nAccount: 0000000000'
                  }
                  onChange={(e) => setBankDetails(e.target.value)}
                />
                <FieldDescription>
                  The payment instructions block at the bottom of the PDF.
                </FieldDescription>
              </Field>
              <Field orientation="horizontal">
                <Switch
                  id="prof-default"
                  checked={isDefault}
                  onCheckedChange={(v: boolean) => setIsDefault(v)}
                />
                <FieldLabel htmlFor="prof-default" className="font-normal">
                  Preselect this profile for new invoices
                </FieldLabel>
              </Field>
            </FieldGroup>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditing(null)}
              >
                Back
              </Button>
              <PendingButton
                type="submit"
                pending={save.isPending}
                disabled={!name.trim()}
              >
                Save profile
              </PendingButton>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

/**
 * Drag-and-drop logo picker built from primitives (the shadcn registry has no
 * file-upload component): the invisible native input covers the whole dashed
 * target, so click, keyboard, and OS drag-and-drop all hit the real control.
 */
function LogoDropzone({
  logo,
  onChange,
}: {
  logo: string | null
  onChange: (logo: string | null) => void
}) {
  const [dragging, setDragging] = useState(false)

  async function onFile(file: File | undefined) {
    if (!file) return
    try {
      onChange(await fileToPngDataUrl(file))
    } catch (e) {
      toast.error('Could not read the image', {
        description: (e as Error).message,
      })
    }
  }

  return (
    <div
      className={cn(
        'relative rounded-md border border-dashed p-4 transition-colors',
        dragging && 'border-ring bg-accent',
      )}
    >
      {/* The overlay input is the drop target, so the drag styling hooks
          live on it — the wrapper stays a passive container. */}
      <input
        type="file"
        accept="image/png,image/jpeg,image/svg+xml"
        aria-label="Upload logo"
        className="absolute inset-0 cursor-pointer opacity-0"
        onDragOver={() => setDragging(true)}
        onDragLeave={() => setDragging(false)}
        onDrop={() => setDragging(false)}
        onChange={(e) => {
          void onFile(e.target.files?.[0])
          e.target.value = ''
        }}
      />
      <div className="pointer-events-none flex items-center gap-3">
        {logo ? (
          <img
            src={logo}
            alt="Logo preview"
            className="h-12 max-w-40 rounded border bg-white object-contain p-1"
          />
        ) : (
          <ImageUp className="size-6 shrink-0 text-muted-foreground" />
        )}
        <div className="min-w-0 text-sm">
          <p>
            {logo
              ? 'Drop or click to replace'
              : 'Drop an image here, or click to upload'}
          </p>
          <p className="text-muted-foreground text-xs">
            PNG, JPEG, or SVG. Drawn at the top left of the PDF.
          </p>
        </div>
      </div>
      {logo ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute top-2 right-2"
          onClick={() => onChange(null)}
        >
          Remove
        </Button>
      ) : null}
    </div>
  )
}

/** PNG/JPEG pass through as data URLs; SVG is rasterized at 2x via canvas so
 *  the stored logo is always something jsPDF can draw. */
async function fileToPngDataUrl(file: File): Promise<string> {
  if (file.type === 'image/png' || file.type === 'image/jpeg') {
    const url = await new Promise<string>((resolve, reject) => {
      const r = new FileReader()
      r.onload = () => resolve(String(r.result))
      r.onerror = () => reject(new Error('unreadable file'))
      r.readAsDataURL(file)
    })
    if (url.length > 400_000) {
      throw new Error('Image is too large. Use one under about 250KB.')
    }
    return url
  }
  if (file.type !== 'image/svg+xml') {
    throw new Error('Use a PNG, JPEG, or SVG image.')
  }
  const svgUrl = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image()
      i.onload = () => resolve(i)
      i.onerror = () => reject(new Error('unreadable SVG'))
      i.src = svgUrl
    })
    const width = img.naturalWidth || 480
    const height = img.naturalHeight || 160
    const scale = Math.min(2, 960 / width)
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(width * scale)
    canvas.height = Math.round(height * scale)
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('canvas unavailable')
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    return canvas.toDataURL('image/png')
  } finally {
    URL.revokeObjectURL(svgUrl)
  }
}
